import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Paper, Grid, Button, FormControl, Select, MenuItem } from '@material-ui/core';
import { Tab, Tabs } from '@material-ui/core';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import TabPanel from 'components/TabPanel';
import { useLocation } from 'react-router-dom';
import { useStylesSecond } from './styles';
import WebAssetIcon from '@material-ui/icons/WebAsset';
import { getFieldConfig } from '../../../utils/fieldConfigUtils';

// Add type definitions for field configuration
// interface FieldConfig {
//   destKey?: string;
//   destLabel?: string;
//   sourseKey?: string;
//   sourceKey?: string;
//   sourseLabel?: string;
//   sourceLabel?: string;
//   isFromPreference?: boolean;
//   defaultValue?: string;
// }
function useQuerys() {
  return new URLSearchParams(useLocation().search);
}

export const MicrosoftInfoTable: FC = observer(() => {
  const classes = {
    ...useStyles(),
    ...useStylesSecond(),
  };
  const [infoAttributeMappings, setInfoAttributeMappings] = useState<{ [key: string]: { [key: string]: string } }>({
    people: {},
    group: {},
    department: {},
  });
  const {
    AppStore: { getAttributePreferences, AttributePreferences },
    notificationStore: { openNotification },
  } = useStore();

  useEffect(() => {
    document.title = 'EasyIdentity: App: InfoDetail';
  }, []);

  const getAttributePreferencesReq = useQuery(
    'getAttributePreferencesReq',
    () => getAttributePreferences('microsoft'),
    {
      onError: () => openNotification('error', `Can't get attribute`),
    }
  );

  const infoData = getAttributePreferencesReq?.data?.data;

  const getAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];
    return infoData.attributes.filter((attr: any) => attr.entity === entity);
  };

  const getMicrosoftAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];
    return infoData.attributes
      .filter((attr: any) => attr.entity === entity)
      .map((attr: any) => {
        // Always use default data for dropdown options, not preference
        const defaultConfig = attr.default || {};
        return {
          ...attr,
          destKey: defaultConfig.destKey,
          destLabel: defaultConfig.destLabel,
          sourseKey: defaultConfig.sourseKey || defaultConfig.sourceKey,
          sourseLabel: defaultConfig.sourseLabel || defaultConfig.sourceLabel,
          isFromDefault: true,
        };
      })
      .filter((attr: any) => attr.sourseKey || attr.sourceKey); // Only include items with sourseKey or sourceKey
  };

  const isAttributeEditable = (destKey: string) => {
    return true; // All fields are editable except Microsoft fixed fields
  };

  const isMicrosoftAttributeFixed = (sourseKey: string) => {
    const fixedMicrosoftFields = [
      'primaryEmail',
      'userPrincipalName', // Email - fixed
      'password',
      'id',
      'email',
      // Password - fixed
    ];
    return fixedMicrosoftFields.includes(sourseKey);
  };

  const AttributePreferencesReq = useMutation((data: any) => AttributePreferences(data), {
    mutationKey: 'AttributePreferencesReq',
    onSuccess: (data) => {
      openNotification('success', 'Attribute mappings saved!');
      getAttributePreferencesReq.refetch(); // Refresh the data
      setInfoAttributeMappings({
        people: {},
        group: {},
        department: {},
      });
    },
    onError: (error) => {
      openNotification('error', 'Failed to save attribute mappings');
    },
  });

  function a11yProps(index: string) {
    return {
      id: `wrapped-tab-${index}`,
      'aria-controls': `wrapped-tabpanel-${index}`,
    };
  }
  const query = useQuerys();
  const queryValue = query.get('value');
  const defaultTabs = (Array.isArray(queryValue) ? queryValue[0] : queryValue) ?? 'one';

  const [value, setValues] = useState<string>(defaultTabs);

  const handleChange = (event: ChangeEvent<unknown>, newValue: string) => {
    setValues(newValue);
  };

  const getCurrentInfoTabMappings = () => {
    if (value === 'one') return infoAttributeMappings.people || {};
    if (value === 'two') return infoAttributeMappings.group || {};
    if (value === 'three') return infoAttributeMappings.department || {};
    return {};
  };

  const handleInfoAttributeMapping = (destKey: string, sourseKey: string) => {
    const tabKey = value === 'one' ? 'people' : value === 'two' ? 'group' : 'department';
    setInfoAttributeMappings((prev) => {
      const newMappings = { ...prev };

      // Clear the selected value from ALL other fields first to prevent duplicates
      if (sourseKey && sourseKey !== 'none') {
        Object.keys(newMappings[tabKey]).forEach((key) => {
          if (key !== destKey && newMappings[tabKey][key] === sourseKey) {
            newMappings[tabKey][key] = '';
          }
        });
      }

      // Set the new mapping
      newMappings[tabKey] = {
        ...newMappings[tabKey],
        [destKey]: sourseKey === 'none' ? '' : sourseKey,
      };
      return newMappings;
    });
  };

  const saveInfoAttributeMappings = () => {
    const allEntities = ['people', 'group', 'department'];
    const allRequiredFields: any[] = [];

    allEntities.forEach((entity) => {
      const entityAttributes = getAttributesByEntity(entity);
      const requiredFields = entityAttributes
        .filter((attr: any) => attr.required)
        .filter((attr: any) => {
          const fieldConfig = getFieldConfig(attr);
          if (!fieldConfig.destKey) return false;
          const tabKey = entity;
          return !infoAttributeMappings[tabKey]?.[fieldConfig.destKey];
        });

      allRequiredFields.push(...requiredFields.map((field: any) => ({ ...field, entity })));
    });

    if (allRequiredFields.length > 0) {
      const fieldNames = allRequiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return `${fieldConfig.destLabel || 'Unknown'} (${field.entity})`;
        })
        .join(', ');
      openNotification('error', `Please map all required fields: ${fieldNames}`);
      return;
    }

    const payload = {
      app_name: 'microsoft',
      attributes: [] as any[],
    };

    const allMappings = {
      people: infoAttributeMappings.people || {},
      group: infoAttributeMappings.group || {},
      department: infoAttributeMappings.department || {},
    };

    Object.entries(allMappings).forEach(([entity, mappings]) => {
      const allEntityAttributes = getAttributesByEntity(entity);
      allEntityAttributes.forEach((originalAttr: any) => {
        const fieldConfig = getFieldConfig(originalAttr);
        let destKey = fieldConfig.destKey;
        if (!destKey && originalAttr.default) {
          // Check if any property in default looks like a destKey
          Object.keys(originalAttr.default).forEach((key) => {
            if (
              key !== 'destKey' &&
              key !== 'destLabel' &&
              key !== 'sourseKey' &&
              key !== 'sourseLabel' &&
              key !== 'defaultValue'
            ) {
              // This might be the destKey stored in a different property
              destKey = key;
            }
          });
        }

        if (destKey) {
          const sourseKey = mappings[destKey] || '';
          const selectedMicrosoftAttr = sourseKey
            ? getMicrosoftAttributesByEntity(entity).find((attr: any) => attr.sourseKey === sourseKey)
            : null;

          const attributeItem = {
            type: originalAttr.type,
            entity,
            hidden: originalAttr.hidden,
            required: originalAttr.required,
            default: originalAttr.default, // Include original default
            preference: {
              destKey,
              destLabel: fieldConfig.destLabel || '',
              sourseKey: sourseKey || '', // Include empty string to clear mapping
              sourseLabel: selectedMicrosoftAttr?.sourseLabel || sourseKey || '',
            },
          };
          payload.attributes.push(attributeItem);
        } else {
          console.log('Skipping attribute (no destKey):', originalAttr);
        }
      });
    });
    AttributePreferencesReq.mutate(payload);
  };

  // Initialize mappings from API data
  useEffect(() => {
    if (infoData?.attributes) {
      const mappings = {
        people: {} as { [key: string]: string },
        group: {} as { [key: string]: string },
        department: {} as { [key: string]: string },
      };

      infoData.attributes.forEach((attr: any) => {
        // Use utility function to get the correct configuration
        const fieldConfig = getFieldConfig(attr);
        if (fieldConfig.destKey) {
          mappings[attr.entity as keyof typeof mappings][fieldConfig.destKey] =
            fieldConfig.sourseKey || fieldConfig.sourceKey || '';
        }
      });

      setInfoAttributeMappings(mappings);
    }
  }, [infoData]);

  const isMicrosoftAttributeDisabled = (microsoftKey: string, currentLdapKey: string) => {
    // Allow all selections - no restrictions on duplicates
    return false;
  };

  const handleNext = () => {
    const currentEntity = value === 'one' ? 'people' : 'group';
    const requiredFields = getAttributesByEntity(currentEntity)
      .filter((attr: any) => attr.required)
      .filter((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (!fieldConfig.destKey) return false;
        return !getCurrentInfoTabMappings()[fieldConfig.destKey];
      });

    if (requiredFields.length > 0) {
      const fieldNames = requiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return fieldConfig.destLabel || 'Unknown';
        })
        .join(', ');
      openNotification('error', `Please map required fields: ${fieldNames}`);
      return;
    }

    if (value === 'one') setValues('two');
  };

  const handleBack = () => {
    if (value === 'two') setValues('one');
  };

  const isLastTab = value === 'two';
  const isFirstTab = value === 'one';

  useAppPreloader(AttributePreferencesReq.isLoading || getAttributePreferencesReq.isFetching);
  return (
    <>
      <div className={classes.mappingHeader}>
        <h2 className={classes.mappingTitle}>Configure Attribute Mapping</h2>
        <p className={classes.mappingSubText}>
          Map Easyidentity attributes to their corresponding Microsoft Entra / Office 365 Workspace fields.
        </p>
      </div>
      <Grid item xl className={classes.tabsPanels}>
        <Paper className={classes.tabsPaper}>
          <Tabs
            value={value}
            className={classes.tabs}
            TabIndicatorProps={{ style: { color: 'transparent', background: 'transparent' } }}
            onChange={handleChange}
          >
            <Tab value='one' wrapped label={<span>Users</span>} {...a11yProps('one')} className={classes.label} />
            <Tab value='two' wrapped label={<span>Groups</span>} {...a11yProps('two')} className={classes.label} />
          </Tabs>

          <div className={classes.tabBox}>
            <TabPanel value={value} index='one'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='user attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                        Microsoft Entra / Office 365 Attributes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('people')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isMicrosoftAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }`}
                                    value={getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || ''}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value='none'>None</MenuItem>
                                    {getMicrosoftAttributesByEntity('people').map((option: any, optIndex: number) => (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={
                                          isMicrosoftAttributeFixed(option.sourseKey) ||
                                          isMicrosoftAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                        }
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isMicrosoftAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index='two'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='group attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                        Microsoft Entra / Office 365 Attributes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('group')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isMicrosoftAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }`}
                                    value={getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || ''}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value='none'>None</MenuItem>
                                    {getMicrosoftAttributesByEntity('group').map((option: any, optIndex: number) => (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={
                                          isMicrosoftAttributeFixed(option.sourseKey) ||
                                          isMicrosoftAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                        }
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isMicrosoftAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
          </div>
        </Paper>
      </Grid>
    </>
  );
});
